import React from "react";
import type { AbilityScores } from "../../engine/types/entity.js";
import {
  decrement,
  increment,
  MAX_SCORE,
  MIN_SCORE,
  POINT_POOL,
  randomAllocation,
  remainingPoints,
  scoreCost,
  validateAllocation,
} from "../../engine/chargen/pointBuy.js";

/**
 * Character-creator point-buy allocation panel.
 *
 * Controlled component — parent owns the AbilityScores state so the wizard
 * (C10) can persist the allocation across steps. Each +/- button dispatches
 * the pure `increment` / `decrement` helpers so the rules stay in the
 * engine, not duplicated here.
 */

interface PointBuyPanelProps {
  readonly scores: AbilityScores;
  readonly onChange: (next: AbilityScores) => void;
  /** Point pool override; defaults to the 5e SRD 27. */
  readonly pool?: number;
}

const ABILITY_LABELS: Readonly<Record<keyof AbilityScores, string>> = {
  STR: "Strength",
  DEX: "Dexterity",
  CON: "Constitution",
  INT: "Intelligence",
  WIS: "Wisdom",
  CHA: "Charisma",
};

const ORDER: readonly (keyof AbilityScores)[] = ["STR", "DEX", "CON", "INT", "WIS", "CHA"];

export function PointBuyPanel({ scores, onChange, pool = POINT_POOL }: PointBuyPanelProps) {
  const remaining = remainingPoints(scores, pool);
  const validation = validateAllocation(scores, pool);

  return (
    <div
      style={{
        fontFamily: "monospace",
        color: "white",
        background: "rgba(0,0,0,0.8)",
        border: "1px solid rgba(255,255,255,0.2)",
        borderRadius: 4,
        padding: 12,
        pointerEvents: "auto",
        width: 320,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <strong>Point buy</strong>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            data-testid="point-buy-random"
            onClick={() => onChange(randomAllocation(pool))}
            title="Spend the full pool on a random allocation"
            style={{
              background: "#333",
              color: "white",
              border: "1px solid #555",
              borderRadius: 3,
              fontFamily: "monospace",
              fontSize: 11,
              padding: "2px 8px",
              cursor: "pointer",
            }}
          >
            🎲 Random
          </button>
          <span
            data-testid="point-buy-remaining"
            style={{ color: remaining < 0 ? "#f44336" : remaining === 0 ? "#4caf50" : "#ffcc00" }}
          >
            {remaining} / {pool}
          </span>
        </div>
      </div>
      {ORDER.map((ability) => {
        const score = scores[ability];
        const cost = scoreCost(score) ?? 0;
        const canInc = increment(scores, ability, pool) !== scores;
        const canDec = decrement(scores, ability) !== scores;
        return (
          <div
            key={ability}
            style={{
              display: "grid",
              gridTemplateColumns: "100px 1fr 32px 40px 32px 48px",
              alignItems: "center",
              gap: 4,
              marginTop: 4,
            }}
          >
            <span>{ABILITY_LABELS[ability]}</span>
            <span style={{ color: "#888", fontSize: 11 }}>
              ({MIN_SCORE}–{MAX_SCORE})
            </span>
            <button
              data-testid={`point-buy-dec-${ability}`}
              onClick={() => onChange(decrement(scores, ability))}
              disabled={!canDec}
              style={{ background: "#333", color: "white", border: "1px solid #555", fontFamily: "monospace" }}
            >
              −
            </button>
            <span data-testid={`point-buy-${ability}`} style={{ textAlign: "center" }}>
              {score}
            </span>
            <button
              data-testid={`point-buy-inc-${ability}`}
              onClick={() => onChange(increment(scores, ability, pool))}
              disabled={!canInc}
              style={{ background: "#333", color: "white", border: "1px solid #555", fontFamily: "monospace" }}
            >
              +
            </button>
            <span style={{ color: "#888", fontSize: 11 }}>cost {cost}</span>
          </div>
        );
      })}
      {!validation.valid && (
        <div style={{ color: "#f44336", fontSize: 11, marginTop: 8 }}>
          {validation.issues.map((issue, i) => (
            <div key={i}>
              {issue.kind === "out_of_range"
                ? `${issue.ability} ${issue.score} is outside ${MIN_SCORE}–${MAX_SCORE}`
                : `Over budget: spent ${issue.spent} / ${issue.pool}`}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
